import { Injectable } from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { BehaviorSubject, Observable } from "rxjs";
import {
  Rule,
  PredicateFilter,
  ReturnResponse,
  REMethodNames,
  REMethodParameters,
} from "../rule-config-component/models/rule-engine";
import { HttpClient } from "@angular/common/http";
import { AppConfigService } from "../configurations/app-config.service";
/** Allowed characters in rule names (letters, digits, underscore, space). */
export const RULE_NAME_PATTERN = /^[a-zA-Z0-9_ ]+$/;

export const RULE_NAME_ALREADY_EXISTS_MESSAGE = "Rule name already exists";

export const RULE_NAME_INVALID_CHARS_MESSAGE =
  "Rule name may only contain letters, numbers, spaces, and underscore.";

export const RULE_NAME_RESERVED_MESSAGE = "Rule name cannot be AND or OR.";

export function isReservedRuleName(ruleName: string): boolean {
  const normalized = normalizeRuleName(ruleName).toUpperCase();
  return normalized === "AND" || normalized === "OR";
}

export function isValidRuleName(ruleName: unknown): boolean {
  const normalized = normalizeRuleName(ruleName);
  if (!normalized) {
    return false;
  }
  if (!RULE_NAME_PATTERN.test(normalized)) {
    return false;
  }
  return !isReservedRuleName(normalized);
}

export function getRuleNameValidationMessage(ruleName: unknown): string | null {
  const normalized = normalizeRuleName(ruleName);
  if (!normalized) {
    return null;
  }
  if (!RULE_NAME_PATTERN.test(normalized)) {
    return RULE_NAME_INVALID_CHARS_MESSAGE;
  }
  if (isReservedRuleName(normalized)) {
    return RULE_NAME_RESERVED_MESSAGE;
  }
  return null;
}

export function ruleNameFormatValidator(
  control: AbstractControl
): ValidationErrors | null {
  const normalized = normalizeRuleName(control.value);
  if (!normalized) {
    return null;
  }
  if (!RULE_NAME_PATTERN.test(normalized)) {
    return { ruleNameFormat: true };
  }
  if (isReservedRuleName(normalized)) {
    return { ruleNameReserved: true };
  }
  return null;
}
/**
 * Normalizes a rule name for comparison.
 * @example normalizeRuleName('  Rule2  ') // 'Rule2'
 */
export function normalizeRuleName(ruleName: unknown): string {
  return (ruleName ?? "").toString().trim();
}

export interface RuleConfigVersionOptionLike {
  value: string;
  status?: string;
}

export interface RuleConfigSaveTarget {
  targetVersion: number;
  targetStatus: string;
  isSameStatusUpdate: boolean;
  isNewRuleConfigVersion: boolean;
}

/** Minimal rule shape for duplicate name checks (Rule Engine + Routing RuleConfig). */
export interface RuleNameCheckable {
  rulename?: string;
  rulenumber?: number;
}

export interface RuleValueDisplay {
  primary: string;
  extraCount: number;
  fullTitle: string;
}

/**
 * True when the operator supports multiple pipe-delimited values.
 * @example isMultiValueOperator('IN') // true
 */
export function isMultiValueOperator(operator: unknown): boolean {
  const op = (operator ?? "").toString().trim().toUpperCase();
  return op === "IN" || op === "NOT IN" || op === "NOTIN";
}

/**
 * Splits a stored rule value into individual entries (pipe-delimited).
 * @example parseRuleValues('a|b|c') // ['a', 'b', 'c']
 */
export function parseRuleValues(value: unknown): string[] {
  const raw = (value ?? "").toString().trim();
  if (!raw) {
    return [];
  }
  return raw
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Joins chip values into the pipe-delimited storage format.
 * @example joinRuleValues(['a', 'b']) // 'a|b'
 */
export function joinRuleValues(values: string[]): string {
  return (values || [])
    .map((part) => (part ?? "").toString().trim())
    .filter(Boolean)
    .join("|");
}

/**
 * Normalizes a rule value string (trim, collapse spaces around pipes).
 * @example normalizeRuleValue(' a | b ') // 'a|b'
 */
export function normalizeRuleValue(value: unknown): string {
  return (value ?? "")
    .toString()
    .replace(/\s*\|\s*/g, "|")
    .trim();
}

/**
 * Splits a chip input token — supports paste of pipe-separated values.
 * @example splitRuleValueChipToken('a|b|c') // ['a', 'b', 'c']
 */
export function splitRuleValueChipToken(token: unknown): string[] {
  const raw = (token ?? "").toString().trim();
  if (!raw) {
    return [];
  }
  if (raw.includes("|")) {
    return parseRuleValues(raw);
  }
  return [raw];
}

/**
 * Grid display: first value plus count of remaining values.
 * @example formatRuleValueDisplay('a|b|c') // { primary: 'a', extraCount: 2, fullTitle: 'a|b|c' }
 */
export interface AppendRuleValuesResult {
  values: string[];
  skippedDuplicates: number;
  limitReached: boolean;
}

/**
 * Adds one or more values (supports pipe paste) without duplicates, up to maxCount.
 */
export function appendRuleValuesFromInput(
  existing: string[],
  rawInput: unknown,
  maxCount = 100
): AppendRuleValuesResult {
  const parts = splitRuleValueChipToken(rawInput);
  if (!parts.length) {
    return {
      values: [...(existing || [])],
      skippedDuplicates: 0,
      limitReached: false,
    };
  }

  const next = [...(existing || [])];
  let skippedDuplicates = 0;
  let limitReached = false;

  for (const part of parts) {
    if (next.length >= maxCount) {
      limitReached = true;
      break;
    }
    if (next.includes(part)) {
      skippedDuplicates += 1;
      continue;
    }
    next.push(part);
  }

  if (next.length >= maxCount && parts.length > 0) {
    limitReached = true;
  }

  return { values: next, skippedDuplicates, limitReached };
}

export function formatRuleValueDisplay(value: unknown): RuleValueDisplay {
  const fullTitle = normalizeRuleValue(value);
  const parts = parseRuleValues(fullTitle);
  if (!parts.length) {
    return { primary: "", extraCount: 0, fullTitle: "" };
  }
  return {
    primary: parts[0],
    extraCount: Math.max(0, parts.length - 1),
    fullTitle,
  };
}

/**
 * True when another rule in the list already uses this name (case-insensitive).
 * @example isDuplicateRuleName(rules, 'Rule2') // add
 * @example isDuplicateRuleName(rules, 'rule2') // true when 'Rule2' exists
 * @example isDuplicateRuleName(rules, 'Rule2', { excludeRuleNumber: 1 }) // edit rule #1
 * @example isDuplicateRuleName(rules, 'Rule2', { excludeRuleIndex: 0 }) // edit index 0
 */
export function isDuplicateRuleName(
  rules: RuleNameCheckable[],
  ruleName: string,
  options?: {
    excludeRuleNumber?: number | null;
    excludeRuleIndex?: number | null;
  }
): boolean {
  const normalized = normalizeRuleName(ruleName).toLowerCase();
  if (!normalized) {
    return false;
  }
  const excludeRuleNumber = options?.excludeRuleNumber ?? null;
  const excludeRuleIndex = options?.excludeRuleIndex ?? null;

  return (rules || []).some((rule, index) => {
    if (excludeRuleIndex != null && index === excludeRuleIndex) {
      return false;
    }
    if (excludeRuleNumber != null && rule.rulenumber === excludeRuleNumber) {
      return false;
    }
    return normalizeRuleName(rule.rulename).toLowerCase() === normalized;
  });
}

/**
 * Extracts rule tokens from named or legacy numeric expressions (e.g. 1, 1 AND 2, (Rule2)).
 */
export function extractExpressionRuleTokens(expression: string): string[] {
  const trimmed = (expression ?? "").toString().trim();
  if (!trimmed) {
    return [];
  }

  const ruleNamesToReturn: string[] = [];
  const regexSingleRule =
    /^\(\s*[a-zA-Z0-9_]+(?:\s+(?!AND|OR\b)[a-zA-Z0-9_]+)*\s*\)$/i;
  const regexAndOrRule =
    /^\(\s*[a-zA-Z0-9_ ]+(\s+(AND|OR)\s+[a-zA-Z0-9_ ]+)+\s*\)$/i;
  const regexMultipleAndOr =
    /^\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)\s*(AND|OR)\s*\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)(\s*(AND|OR)\s*\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\))*$/i;

  if (regexSingleRule.test(trimmed)) {
    ruleNamesToReturn.push(trimmed.replace(/[()]/g, "").trim());
  } else if (regexAndOrRule.test(trimmed)) {
    const innerExpression = trimmed.slice(1, -1);
    ruleNamesToReturn.push(...innerExpression.split(/\s+(AND|OR)\s+/i));
  } else if (regexMultipleAndOr.test(trimmed)) {
    const regexGroup =
      /\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)/gi;
    const groups = trimmed.match(regexGroup) || [];
    groups.forEach((group) => {
      const innerExpression = group.trim().slice(1, -1);
      ruleNamesToReturn.push(...innerExpression.split(/\s+(AND|OR)\s+/i));
    });
  }

  if (ruleNamesToReturn.length > 0) {
    return ruleNamesToReturn.map((token) => token.trim()).filter(Boolean);
  }

  const flattened = trimmed.replace(/[()]/g, " ").replace(/\s+/g, " ").trim();
  return flattened
    .split(/\s+(?:AND|OR)\s+/i)
    .map((token) => token.trim())
    .filter(Boolean);
}

const NAMED_EXPR_SINGLE_RULE =
  /^\(\s*[a-zA-Z0-9_]+(?:\s+(?!AND|OR\b)[a-zA-Z0-9_]+)*\s*\)$/i;
const NAMED_EXPR_AND_OR_RULE =
  /^\(\s*[a-zA-Z0-9_ ]+(\s+(AND|OR)\s+[a-zA-Z0-9_ ]+)+\s*\)$/i;
const NAMED_EXPR_MULTIPLE_AND_OR =
  /^\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)\s*(AND|OR)\s*\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)(\s*(AND|OR)\s*\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\))*$/i;

function findRuleByExactName(
  rules: RuleNameCheckable[],
  token: string
): RuleNameCheckable | undefined {
  const t = (token ?? "").toString().trim();
  return rules.find((rule) => rule.rulename?.toString() === t);
}

/** Legacy fallback label when API omits rulename — e.g. rulenumber 2 → "Rule2". */
export function defaultRuleDisplayLabel(ruleNumber: number): string {
  return `Rule${ruleNumber}`;
}

/**
 * UI / Build Expression label for a rule number token.
 * @example rulename "test", token "8" → "test"
 * @example rulename "", token "8" → "Rule8"
 */
function ruleNameTokenForDisplay(
  rule: RuleNameCheckable | undefined,
  numberToken: string
): string {
  const name = (rule?.rulename ?? "").toString().trim();
  if (name) {
    return name;
  }
  const n = Number(numberToken);
  if (Number.isFinite(n) && n > 0) {
    return defaultRuleDisplayLabel(n);
  }
  return numberToken;
}

/**
 * Save path: map display token back to API rule number.
 * @example "test" + rule 2 → "2"
 * @example "Rule2" (legacy UI) → "2"
 * @example "2" → "2"
 */
function resolveDisplayTokenToRuleNumber(
  token: string,
  rules: RuleNameCheckable[]
): string {
  const t = (token ?? "").toString().trim();
  if (!t) {
    return t;
  }
  if (/^\d+$/.test(t)) {
    return t;
  }
  const byName = findRuleByExactName(rules, t);
  if (byName?.rulenumber != null) {
    return String(byName.rulenumber);
  }
  const ruleN = t.match(/^Rule(\d+)$/i);
  if (ruleN) {
    return ruleN[1];
  }
  return t;
}

function generateNumberedRuleExpForSingleRule(
  finalNameRuleExp: string,
  rules: RuleNameCheckable[]
): string {
  const ruleName = finalNameRuleExp.replace(/[()]/g, "").trim();
  return `(${resolveDisplayTokenToRuleNumber(ruleName, rules)})`;
}

function generateNumberedRuleExpForAndOrRule(
  finalNameRuleExp: string,
  rules: RuleNameCheckable[]
): string {
  let finalExpression = "";
  const innerExpression = finalNameRuleExp.trim().slice(1, -1);
  const tokens = innerExpression.split(/\s+(AND|OR)\s+/i);

  tokens.forEach((token, index) => {
    if (token.toUpperCase() === "AND" || token.toUpperCase() === "OR") {
      finalExpression += token.toUpperCase();
    } else {
      finalExpression += resolveDisplayTokenToRuleNumber(token, rules);
    }
    if (index < tokens.length - 1) {
      finalExpression += " ";
    }
  });
  return `(${finalExpression.trim()})`;
}

function generateNumberedRuleExpForMultipleAndOr(
  finalNameRuleExp: string,
  rules: RuleNameCheckable[]
): string {
  let finalExpression = "";
  const regexGroup =
    /\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)/gi;
  const regexOperator = /\)\s*(AND|OR)\s*\(/gi;
  const regexToCheckForAndOr = /\(\s*[^()]*\b(AND|OR)\b[^()]*\s*\)/i;
  const groups = finalNameRuleExp.match(regexGroup) || [];
  const operators = [...finalNameRuleExp.matchAll(regexOperator)].map(
    (match) => match[1]
  );

  groups.forEach((group, index) => {
    if (!regexToCheckForAndOr.test(group)) {
      finalExpression += generateNumberedRuleExpForSingleRule(group, rules);
    } else {
      finalExpression += generateNumberedRuleExpForAndOrRule(group, rules);
    }
    finalExpression += " ";
    if (index + 1 < groups.length) {
      finalExpression += operators[index];
    }
    finalExpression += " ";
  });
  return finalExpression.trim();
}

/**
 * Converts a named rule expression to rule numbers for API persistence.
 * Preserves parentheses and AND/OR structure (Rule Config algorithm).
 *
 * @example UI "(test AND testobr)" → API "(2 AND 6)"
 * @example legacy UI "(Rule1 AND Rule2)" → API "(1 AND 2)"
 */
export function convertNamedRuleExpressionToNumbers(
  finalNameRuleExp: string,
  rules: RuleNameCheckable[]
): string {
  const trimmed = (finalNameRuleExp ?? "").toString().trim();
  if (!trimmed || !rules?.length) {
    return trimmed;
  }

  const tokens = extractExpressionRuleTokens(trimmed);
  if (tokens.length > 0 && tokens.every((t) => /^\d+$/.test(t))) {
    return trimmed;
  }

  if (NAMED_EXPR_SINGLE_RULE.test(trimmed)) {
    return generateNumberedRuleExpForSingleRule(trimmed, rules);
  }
  if (NAMED_EXPR_AND_OR_RULE.test(trimmed)) {
    return generateNumberedRuleExpForAndOrRule(trimmed, rules);
  }
  if (NAMED_EXPR_MULTIPLE_AND_OR.test(trimmed)) {
    return generateNumberedRuleExpForMultipleAndOr(trimmed, rules);
  }
  return trimmed;
}

const NUMBERED_EXPR_SINGLE_RULE = /^\([a-zA-Z0-9_]+\)$/i;
const NUMBERED_EXPR_AND_OR_RULE =
  /^\(\s*[a-zA-Z0-9_ ]+(\s+(AND|OR)\s+[a-zA-Z0-9_ ]+)+\s*\)$/i;
const NUMBERED_EXPR_MULTIPLE_AND_OR =
  /^\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)\s*(AND|OR)\s*\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)(\s*(AND|OR)\s*\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\))*$/i;

function findRuleByNumber(
  rules: RuleNameCheckable[],
  token: string
): RuleNameCheckable | undefined {
  const n = Number((token ?? "").toString().trim());
  if (!Number.isFinite(n)) {
    return undefined;
  }
  return rules.find((rule) => Number(rule.rulenumber ?? 0) === n);
}

/** True when API sent at least one non-empty rulename (new configs). */
export function rulesHavePersistedRuleNames(rules: RuleNameCheckable[]): boolean {
  return (rules || []).some((rule) => (rule.rulename ?? "").toString().trim().length > 0);
}

function generateFinalRuleExpForSingleRule(
  finalNumberRuleExp: string,
  rules: RuleNameCheckable[]
): string {
  const ruleNumber = finalNumberRuleExp.replace(/[()]/g, "").trim();
  const rule = findRuleByNumber(rules, ruleNumber);
  return `(${ruleNameTokenForDisplay(rule, ruleNumber)})`;
}

function generateFinalRuleExpForAndOrRule(
  finalNumberRuleExp: string,
  rules: RuleNameCheckable[]
): string {
  let finalExpression = "";
  const innerExpression = finalNumberRuleExp.trim().slice(1, -1);
  const tokens = innerExpression.split(/\s+(AND|OR)\s+/i);

  tokens.forEach((token, index) => {
    if (token.toUpperCase() === "AND" || token.toUpperCase() === "OR") {
      finalExpression += token.toUpperCase();
    } else {
      const rule = findRuleByNumber(rules, token);
      finalExpression += ruleNameTokenForDisplay(rule, token);
    }
    if (index < tokens.length - 1) {
      finalExpression += " ";
    }
  });
  return `(${finalExpression.trim()})`;
}

function generateFinalRuleExpForMultipleAndOr(
  finalNumberRuleExp: string,
  rules: RuleNameCheckable[]
): string {
  let finalExpression = "";
  const regexGroup =
    /\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)/gi;
  const regexOperator = /\)\s*(AND|OR)\s*\(/gi;
  const regexToCheckForAndOr = /\(\s*[^()]*\b(AND|OR)\b[^()]*\s*\)/i;
  const groups = finalNumberRuleExp.match(regexGroup) || [];
  const operators = [...finalNumberRuleExp.matchAll(regexOperator)].map(
    (match) => match[1]
  );

  groups.forEach((group, index) => {
    if (!regexToCheckForAndOr.test(group)) {
      finalExpression += generateFinalRuleExpForSingleRule(group, rules);
    } else {
      finalExpression += generateFinalRuleExpForAndOrRule(group, rules);
    }
    finalExpression += " ";
    if (index + 1 < groups.length) {
      finalExpression += operators[index];
    }
    finalExpression += " ";
  });
  return finalExpression.trim();
}

/**
 * Converts API numeric expression to labels for UI / Build Expression.
 *
 * Case 1 — rulenames present:
 *   API "(2 AND 6)" + [{rulenumber:2,rulename:"test"},…] → "(test AND testobr)"
 *
 * Case 2 — legacy (no rulename in JSON):
 *   API "(1 AND 2)" + [{rulenumber:1}, {rulenumber:2}] → "(Rule1 AND Rule2)"
 */
export function convertNumberedRuleExpressionToNames(
  finalNumberRuleExp: string,
  rules: RuleNameCheckable[]
): string | undefined {
  const trimmed = (finalNumberRuleExp ?? "").toString().trim();
  if (!trimmed || !rules?.length) {
    return trimmed || undefined;
  }

  if (/^\d+$/.test(trimmed)) {
    return generateFinalRuleExpForSingleRule(`(${trimmed})`, rules);
  }
  if (NUMBERED_EXPR_SINGLE_RULE.test(trimmed)) {
    return generateFinalRuleExpForSingleRule(trimmed, rules);
  }
  if (NUMBERED_EXPR_AND_OR_RULE.test(trimmed)) {
    return generateFinalRuleExpForAndOrRule(trimmed, rules);
  }
  if (NUMBERED_EXPR_MULTIPLE_AND_OR.test(trimmed)) {
    return generateFinalRuleExpForMultipleAndOr(trimmed, rules);
  }
  return undefined;
}

/**
 * True when a rule is referenced by name or rule number in a satisfaction expression.
 */
export function isRuleReferencedInExpression(
  rule: RuleNameCheckable,
  expression: string
): boolean {
  const tokens = extractExpressionRuleTokens(expression);
  if (!tokens.length) {
    return false;
  }

  const name = normalizeRuleName(rule.rulename);
  const ruleNumber = rule.rulenumber;
  const effectiveName = name || (ruleNumber != null ? `Rule${ruleNumber}` : "");

  if (
    effectiveName &&
    tokens.some((token) => token.toLowerCase() === effectiveName.toLowerCase())
  ) {
    return true;
  }

  const allNumeric = tokens.every((token) => /^\d+$/.test(token));
  if (allNumeric && ruleNumber != null) {
    return tokens.some((token) => token === String(ruleNumber));
  }

  return false;
}

@Injectable({
  providedIn: "root",
})
export class RuleEngineService {
  private _rulesSubject = new BehaviorSubject<any>(null);
  private _pathSubject = new BehaviorSubject<any>(null);
  private _isExpressionBuilderPopUpSubject = new BehaviorSubject<boolean>(
    false
  );
  private rules: Rule[] = [];
  conditionsAdded: boolean = false;
  oldString: string = "";
  dataToSendForPredicatePopUp: any;
  holdTheBuildExpressionData: any;
  clearXpath: boolean = false;
  clearPredicateData: boolean = false;
  // dataFromThePredicatePopUp: any;
  constructor(private _fb: FormBuilder, private _httpClient : HttpClient) {}
  //behavior subject used for file name
  private fileNameSubject = new BehaviorSubject<string>("");
  private popUpSubject = new BehaviorSubject<boolean>(false);
  private subConditionsSubject = new BehaviorSubject<string[]>([]);
  private updateFieldWithPredicateConditionsSubject =
    new BehaviorSubject<string>("");
  private finalRuleExpressionSubject = new BehaviorSubject<string>("");
  protected apiURL: string = AppConfigService.settings.WebApiURL;
  public header = { withCredentials: true }


  getTheBuilderExpressionData() {
    return this.holdTheBuildExpressionData;
  }

  $getUpdatedFieldWithPredicate =
    this.updateFieldWithPredicateConditionsSubject.asObservable();
  setFieldWithUpdatedPredicate(dataFromThePredicatePopUp: any) {
    if (!this.clearXpath) {
      let fullyUpdatedPathWithConditions = this.pathWithPredicateFilters(
        dataFromThePredicatePopUp,
        this.oldString
      );
      this.updateFieldWithPredicateConditionsSubject.next(
        fullyUpdatedPathWithConditions
      );
    } else {
      this.updateFieldWithPredicateConditionsSubject.next(
        dataFromThePredicatePopUp
      );
      this.clearXpath = false;
    }
  }

  //setting the final rule so that it will be displayed in the main screen
  setFinalRuleExpression(finalRuleExpression: string) {
    this.finalRuleExpressionSubject.next(finalRuleExpression);
  }

  /** Synchronous read of the last emitted final rule expression (UI sync after buildFinalExpression). */
  getCurrentFinalRuleExpression(): string {
    return this.finalRuleExpressionSubject.getValue();
  }

  //get the final rule expression to show in the main screen
  $getFinalRuleExpression = this.finalRuleExpressionSubject.asObservable();

  //updating the behavior subject with new value
  setFileName(fileName: string) {
    this.fileNameSubject.next(fileName);
  }

  //returning the observable to subscribe it to get the latest value
  getFileName() {
    return this.fileNameSubject.asObservable();
  }

  $getPopUpValue = this.popUpSubject.asObservable();
  setPopUpValue(isPopUp: boolean) {
    this.popUpSubject.next(isPopUp);
  }

  $getSubConditionsFor = this.subConditionsSubject.asObservable();

  setSubConditionsFor(field: any, nodesWithLeafNodes: any, nodePaths: any) {
    if (!this.clearPredicateData) {
      // if (nodePaths.includes(field) && this.oldString != field) {
      //   this.oldString = field;
      //   this.dataToSendForPredicatePopUp = this.generateDataForPredicate(
      //     field,
      //     nodesWithLeafNodes
      //   );
      //   this.subConditionsSubject.next(this.dataToSendForPredicatePopUp);
      // } else {
      //here we have to parse the data from the string to form array json and then assign it to "dataToSendForPredicatePopUp" variable
      //write a method to parse the data from the incoming string into form json array. assign the return value of this to the
      this.oldString = field;
      this.subConditionsSubject.next(this.dataToSendForPredicatePopUp);
      // }
    } else {
      this.subConditionsSubject.next([]);
      this.clearPredicateData = false;
    }
  }

  private generatePaths(paths: any): string[] {
    if (!paths || !Array.isArray(paths) || paths.length === 0) {
      return []; // Return an empty array if input is invalid
    }

    let result: string[] = [];
    let currentPath = "";

    for (const path of paths) {
      currentPath = currentPath ? `${currentPath}/${path}` : path;
      result.push(currentPath);
    }

    return result;
  }

  addGroup() {
    return this._fb.group({
      logicalOperator: this._fb.control("AND", Validators.required), // Default to 'AND'
      conditions: this._fb.array([this.createConditionGroup()]), // Start with one empty condition
    });
  }

  createConditionGroup(): FormGroup {
    return this._fb.group({
      attribute: this._fb.control("", Validators.required),
      condition: this._fb.control("", Validators.required),
      value: this._fb.control("", Validators.required),
    });
  }

  pathWithPredicateFilters(json: any[], originalPath: string): string {
    let valuesFromOriginalPath = originalPath.split("/");
    let finalPathToReturn = valuesFromOriginalPath.map(
      (originalNodeName: any) => {
        let predicateNode = json.find(
          (node: any) => node.nodeName === originalNodeName
        );
        if (predicateNode) {
          let predicateCondition = this.generateThePath(predicateNode);
          if (predicateCondition != "") {
            return `${originalNodeName}[${predicateCondition}]`;
          } else {
            return `${originalNodeName}`;
          }
        } else {
          return originalNodeName;
        }
      }
    );
    return finalPathToReturn.join("/");
  }
  generateThePath(predicateNode: any): string {
    const groups = predicateNode.formGroup.get("groupArray") as FormArray;

    return (groups.controls as FormGroup[])
      .map((group: FormGroup, groupIndex: number) => {
        const conditions = group.get("conditions") as FormArray;

        const groupConditions = (conditions.controls as FormControl[])
          .map((condition: FormControl, conditionIndex: number) => {
            // Build individual condition string
            return `${condition.get("attribute")?.value}${
              condition.get("condition")?.value
            }'${condition.get("value")?.value}'`;
          })
          .join(` ${group.get("logicalOperator")?.value} `); // Join conditions within a group

        // Wrap group conditions in parentheses if there are multiple groups or multiple conditions
        return conditions.length > 1 || groups.length > 1
          ? `(${groupConditions})`
          : groupConditions;
      })
      .join(" AND "); // Join groups with 'AND'
  }
  getAllRules() {
    return this._rulesSubject.asObservable();
  }
  setAllTheRules(rules: any) {
    this.rules = [...rules];
    this._rulesSubject.next(this.rules);
  }
  clearAllTheRules() {
    this.rules = [];
    this._rulesSubject.next(this.rules);
  }
  setPath(path: any) {
    this._pathSubject.next(path);
  }
  getPath() {
    return this._pathSubject.asObservable();
  }
  setPopUpValueForExpressionBuilder(isExpressionBuilderPopUp: boolean) {
    this._isExpressionBuilderPopUpSubject.next(isExpressionBuilderPopUp);
  }
  $getPopUpValueForExpressionBuilder =
    this._isExpressionBuilderPopUpSubject.asObservable();

  getMethodParameters(methodName: string): Observable<ReturnResponse<REMethodParameters[]>>{
    return this._httpClient.get<ReturnResponse<REMethodParameters[]>>(`${this.apiURL}api/RuleEngineReports/GetMethodParameters?methodName=${methodName}`)
  }
  getMethodNames() : Observable<ReturnResponse<REMethodNames>>{
    return this._httpClient.get<ReturnResponse<REMethodNames>>(`${this.apiURL}api/RuleEngineReports/GetMethodNames`)
  }
  getCoreCache(body){
    return this._httpClient.post<any>(`${this.apiURL}api/CoreCache/GetAllCache`,body,this.header);
  }

  getClearCache(keys: string[]){
    return this._httpClient.post<any>(`${this.apiURL}api/CoreCache/Deletecorecache`, keys, this.header);
  }

  /**
   * Converts uploaded file content to XML format using HL7 converter API
   * @param fileContent The content of the uploaded file (can be any format)
   * @returns Observable that emits the XML string response
   */
  convertToXml(fileContent: string): Observable<string> {
    const headers = { 'Content-Type': 'text/plain' };
    return this._httpClient.post(`${this.apiURL}api/hl7/convert`, fileContent, { 
      headers: headers, 
      responseType: 'text',
      withCredentials: true
    });
  }

  /**
   * Inserts or updates RuleConfig for a given route.
   * Backend endpoint: api/Routes/InsertAndUpdateRuleConfig
   */
  InsertAndUpdateRuleConfig(body: any): Observable<any> {
    return this._httpClient.post<any>(
      `${this.apiURL}api/RuleConfig/SaveRuleConfig`,
      body,
      this.header
    );
  }

  /**
   * Gets all RuleConfig versions for a given route.
   * routeGuid is typed as number (long) to match the API.
   * Backend endpoint: api/RuleConfig/GetRuleConfigVersions
   */
  GetRuleConfigVersions(routeGuid: any, ruleExecutionStage: string): Observable<any> {
    return this._httpClient.get<any>(
      `${this.apiURL}api/RuleConfig/GetRuleConfigVersions?routeUniqueID=${routeGuid}&ruleExecutionStage=${encodeURIComponent(ruleExecutionStage)}`,
      this.header
    );
  }

  /**
   * Lightweight PreValidation source flags (Rule Engine mapping vs legacy validationconfig).
   * Backend: api/RuleConfig/GetPreValidationSourceFlags
   */
  GetPreValidationSourceFlags(routeUniqueID: any, ruleExecutionStage: string = 'PreValidation'): Observable<{
    HasRuleEnginePreValidation: boolean;
    HasLegacyPreValidation: boolean;
  }> {
    const stage = encodeURIComponent(ruleExecutionStage || 'PreValidation');
    return this._httpClient.get<{
      HasRuleEnginePreValidation: boolean;
      HasLegacyPreValidation: boolean;
    }>(
      `${this.apiURL}api/RuleConfig/GetPreValidationSourceFlags?routeUniqueID=${routeUniqueID}&ruleExecutionStage=${stage}`,
      this.header
    );
  }

  /**
   * Saves a version note (QC or Approve). Called only when user clicks Save in the QC/Approve overlay.
   * Pass a single object with keys: RouteUniqueID, RouteRuleMappingID, Notes, UserID, Role, CreatedDate.
   */
  InsertRuleConfigVersionNotes(body: any): Observable<any> {
    return this._httpClient.post<any>(
      `${this.apiURL}api/RuleConfig/InsertRuleConfigVersionNotes`,
      body,
      this.header
    );
  }

  /**
   * Gets version notes for display. Called when opening the QC/Approve/Reject overlay to load existing notes.
   * Pass a single object with routeUniqueID and routeRuleMappingID.
   */
  GetRuleConfigVersionNotes(params: any): Observable<any> {
    const routeUniqueID = params?.routeUniqueID ;
    const routeRuleMappingID = params?.routeRuleMappingID ;
    return this._httpClient.get<any>(
      `${this.apiURL}api/RuleConfig/GetRuleConfigVersionNotes?routeUniqueID=${routeUniqueID}&routeRuleMappingID=${routeRuleMappingID}`,
      this.header
    );
  }

  /**
   * Returns default left/right version values for Compare overlay given a versionOptions array.
   * - If 2+ versions: left = second-to-last, right = last
   * - If 1 version: left = right = that version
   * - If 0 versions: both null
   */
  getDefaultCompareVersions(versionOptions: { value: string }[] | any[]): {
    left: string | null;
    right: string | null;
  } {
    if (!versionOptions || versionOptions.length === 0) {
      return { left: null, right: null };
    }
    if (versionOptions.length === 1) {
      const v = versionOptions[0]?.value ?? null;
      return { left: v, right: v };
    }
    const last = versionOptions[versionOptions.length - 1]?.value ?? null;
    const secondLast = versionOptions[versionOptions.length - 2]?.value ?? last;
    return { left: secondLast, right: last };
  }

  /**
   * Utility to get pretty JSON for a given version value from a versionOptions array.
   * configKey is the property that holds the RuleConfig JSON/string (e.g. 'configdata' or 'ruleConfig').
   */
  /**
   * Resolves which version row Save will create or update (latest entry drives Dev/QC overwrite vs new Dev).
   */
  resolveRuleConfigSaveTarget(
    versionOptions: RuleConfigVersionOptionLike[]
  ): RuleConfigSaveTarget {
    const hasVersions = versionOptions && versionOptions.length > 0;
    let targetVersion = 1;
    let targetStatus = "Dev";
    let isSameStatusUpdate = false;
    let previousLatestNumericVersion = 0;

    if (hasVersions) {
      const latest = versionOptions[versionOptions.length - 1];
      const currentVersion = parseInt(latest.value, 10) || 0;
      previousLatestNumericVersion = currentVersion || 0;
      const status = (latest.status ?? "").toString().trim().toLowerCase();

      if (status === "dev" || status === "qc") {
        targetVersion = currentVersion || 1;
        targetStatus =
          status === "qc"
            ? "QC"
            : ((latest.status ?? "").toString().trim() || "Dev");
        isSameStatusUpdate = true;
      } else if (status === "live") {
        targetVersion = (currentVersion || 0) + 1;
        targetStatus = "Dev";
      } else {
        targetVersion = currentVersion || 1;
        targetStatus = "Dev";
      }
    }

    const isNewRuleConfigVersion =
      !isSameStatusUpdate &&
      (!hasVersions || targetVersion > previousLatestNumericVersion);

    return {
      targetVersion,
      targetStatus,
      isSameStatusUpdate,
      isNewRuleConfigVersion,
    };
  }

  /**
   * True when Save will update Dev/QC while the user is viewing a different version (e.g. Live/Archived).
   */
  shouldConfirmDevQcOverwriteOnSave(
    selectedVersion: string | null | undefined,
    target: RuleConfigSaveTarget
  ): boolean {
    if (
      !target.isSameStatusUpdate ||
      selectedVersion == null ||
      selectedVersion === ""
    ) {
      return false;
    }
    return selectedVersion.toString() !== target.targetVersion.toString();
  }

  /** Version label without dates — e.g. "Version 4 Live" or "Version 6 QC". */
  formatRuleConfigVersionLabel(
    version: string | number | null | undefined,
    status?: string | null
  ): string {
    const v = (version ?? "").toString().trim();
    const s = (status ?? "").toString().trim();
    if (!v) {
      return "the selected version";
    }
    return s ? `Version ${v} ${s}` : `Version ${v}`;
  }

  buildRuleConfigOverwriteConfirmLabels(
    viewingVersion: string | null | undefined,
    viewingStatus: string | null | undefined,
    targetVersion: number,
    targetStatus: string
  ): { targetLabel: string; viewingLabel: string } {
    return {
      targetLabel: this.formatRuleConfigVersionLabel(
        targetVersion,
        targetStatus
      ),
      viewingLabel: this.formatRuleConfigVersionLabel(
        viewingVersion,
        viewingStatus
      ),
    };
  }

  getRuleConfigJsonForVersionFromOptions(
    versionOptions: any[],
    versionValue: string | null,
    configKey: string
  ): string {
    if (!versionValue || !Array.isArray(versionOptions)) {
      return "{}";
    }
    const option = versionOptions.find(
      (o) => o?.value?.toString() === String(versionValue)
    );
    const configdata = option ? option[configKey] : null;
    if (configdata == null) {
      return "{}";
    }
    try {
      const parsed =
        typeof configdata === "string" ? JSON.parse(configdata) : configdata;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return typeof configdata === "string"
        ? configdata
        : JSON.stringify(configdata);
    }
  }

  /** True when the rule is referenced in a build / satisfaction expression. */
  isRuleInBuildExpression(
    rule: RuleNameCheckable,
    expression: string
  ): boolean {
    return isRuleReferencedInExpression(rule, expression ?? "");
  }

  /** True when the rule is referenced in a pipeline block's build expression. */
  isRuleInBlockBuildExpression(
    rule: RuleNameCheckable,
    block: { ruleSatisfaction?: unknown }
  ): boolean {
    return isRuleReferencedInExpression(
      rule,
      (block.ruleSatisfaction ?? "").toString()
    );
  }

  /** False when the rule appears in the expression (delete is blocked). */
  canDeleteRule(rule: RuleNameCheckable, expression: string): boolean {
    const trimmed = (expression ?? "").toString().trim();
    if (!trimmed) {
      return true;
    }
    return !isRuleReferencedInExpression(rule, trimmed);
  }

  isMultiValueOperator(operator: unknown): boolean {
    return isMultiValueOperator(operator);
  }

  parseRuleValues(value: unknown): string[] {
    return parseRuleValues(value);
  }

  joinRuleValues(values: string[]): string {
    return joinRuleValues(values);
  }

  normalizeRuleValue(value: unknown): string {
    return normalizeRuleValue(value);
  }

  splitRuleValueChipToken(token: unknown): string[] {
    return splitRuleValueChipToken(token);
  }

  formatRuleValueDisplay(value: unknown): RuleValueDisplay {
    return formatRuleValueDisplay(value);
  }

  appendRuleValuesFromInput(
    existing: string[],
    rawInput: unknown,
    maxCount = 100
  ): AppendRuleValuesResult {
    return appendRuleValuesFromInput(existing, rawInput, maxCount);
  }
}